import { useEffect } from 'react';
import type { ReactNode } from 'react';

interface Props {
  open: boolean;
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'OK',
  cancelLabel = 'キャンセル',
  danger = false,
  loading = false,
  onConfirm,
  onCancel,
}: Props) {
  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading) onCancel();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, loading, onCancel]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/40 animate-[fade-in_0.2s_ease-out]" onClick={loading ? undefined : onCancel} />
      <div className="relative w-full max-w-sm rounded-lg border border-background-200 bg-background-50 p-5 animate-[scale-in_0.2s_ease-out]">
        <div className="mb-3 flex items-center gap-2">
          <span
            className={`flex h-8 w-8 items-center justify-center rounded-md ${
              danger ? 'bg-accent-100 text-accent-700' : 'bg-primary-100 text-primary-700'
            }`}
          >
            <i className={danger ? 'ri-alert-line' : 'ri-question-line'} />
          </span>
          <h3 className="font-heading text-sm font-semibold text-foreground-950">{title}</h3>
        </div>
        {message && <div className="mb-5 pl-10 text-sm text-foreground-600">{message}</div>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 disabled:opacity-60"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-background-50 transition-colors disabled:opacity-60 ${
              danger ? 'bg-accent-600 hover:bg-accent-700' : 'bg-primary-500 hover:bg-primary-600'
            }`}
          >
            {loading && <i className="ri-loader-4-line animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}